import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { t } from "i18next";
import { useState } from "react";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreTableEnhanced from "../../../app/core/components/CoreTable/CoreTableEnhanced";
import CoreTextArea from "../../../app/core/components/CoreTextArea/CoreTextArea";

const ServiceTaskManager = ({
  tasks,
  onTasksChange,
  disabled = false,
  // Nuove props per gestire le operazioni asincrone
  onAddTask,
  onEditTask,
  onDeleteTask,
  isLoadingOperations = false,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    notes: "", // Cambiato da "note" a "notes" per allinearsi al DTO
    estimatedEffort: "",
  });
  const [validationErrors, setValidationErrors] = useState({
    name: "",
    description: "",
  });

  // Definizione colonne per la tabella con supporto per traduzione
  const columns = [
    { id: "name", label: t("Name") },
    { id: "description", label: t("Description") },
    { id: "notes", label: t("Note") }, // Cambiato da "note" a "notes"
    { id: "estimatedEffort", label: t("EstimatedEffort") },
  ];

  // Preparazione dati per la tabella
  const tableRows = (tasks || []).map((task, index) => ({
    id: task.id || `temp-${index}`,
    name: task.name || "-",
    description: task.description || "-",
    notes: task.notes || "-", // Cambiato da "note" a "notes"
    estimatedEffort: task.estimatedEffort ? `${task.estimatedEffort}h` : "-",
    originalTask: task,
  }));

  // Azioni per la tabella
  const tableActions = [
    {
      icon: <EditIcon />,
      tooltip: t("Edit"),
      onClick: (row) => handleEditTask(row.originalTask),
      disabled: () => disabled || isLoadingOperations,
    },
    {
      icon: <DeleteIcon />,
      tooltip: t("Delete"),
      onClick: (row) => handleDeleteTask(row.originalTask),
      disabled: () => disabled || isLoadingOperations,
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: "" });
    }
  };

  const handleAddNewTask = () => {
    setEditingTask(null);
    setFormData({
      name: "",
      description: "",
      notes: "", // Cambiato da "note" a "notes"
      estimatedEffort: "",
    });
    setValidationErrors({
      name: "",
      description: "",
    });
    setOpenDialog(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setFormData({
      name: task.name || "",
      description: task.description || "",
      notes: task.notes || "", // Cambiato da "note" a "notes"
      estimatedEffort: task.estimatedEffort || "",
    });
    setValidationErrors({
      name: "",
      description: "",
    });
    setOpenDialog(true);
  };

  const handleDeleteTask = async (taskToDelete) => {
    if (onDeleteTask) {
      // Modalità con callback asincrono (editing)
      await onDeleteTask(taskToDelete);
    } else {
      // Modalità sincrona (creazione)
      const updatedTasks = tasks.filter((task, index) =>
        task.id
          ? task.id !== taskToDelete.id
          : index !== tasks.indexOf(taskToDelete)
      );
      onTasksChange(updatedTasks);
    }
  };

  const handleSaveTask = async () => {
    // Validazione
    const errors = {};
    if (!formData.name.trim()) errors.name = t("FieldRequired");
    if (!formData.description.trim()) errors.description = t("FieldRequired");

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Validazione estimatedEffort (deve essere un numero positivo se inserito)
    if (
      formData.estimatedEffort &&
      (isNaN(formData.estimatedEffort) ||
        parseFloat(formData.estimatedEffort) < 0)
    ) {
      setValidationErrors({
        ...errors,
        estimatedEffort: t("EnterValidNumber"),
      });
      return;
    }

    const taskData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      notes: formData.notes.trim(), // Cambiato da "note" a "notes"
      estimatedEffort: formData.estimatedEffort
        ? parseFloat(formData.estimatedEffort)
        : null,
    };

    if (editingTask) {
      // Modifica task esistente
      if (onEditTask) {
        // Modalità con callback asincrono (editing)
        await onEditTask(editingTask, taskData);
      } else {
        // Modalità sincrona (creazione)
        const updatedTasks = tasks.map((task) => {
          if (task.id && editingTask.id) {
            return task.id === editingTask.id ? { ...task, ...taskData } : task;
          } else {
            return task === editingTask ? { ...task, ...taskData } : task;
          }
        });
        onTasksChange(updatedTasks);
      }
    } else {
      // Aggiungi nuova task
      if (onAddTask) {
        // Modalità con callback asincrono (editing)
        await onAddTask(taskData);
      } else {
        // Modalità sincrona (creazione)
        const newTask = {
          id: `temp-${Date.now()}`, // ID temporaneo per nuove task
          ...taskData,
        };
        const updatedTasks = [...tasks, newTask];
        onTasksChange(updatedTasks);
      }
    }

    setOpenDialog(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTask(null);
    setFormData({
      name: "",
      description: "",
      notes: "", // Cambiato da "note" a "notes"
      estimatedEffort: "",
    });
    setValidationErrors({
      name: "",
      description: "",
    });
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="m-0">{t("ServiceTasks")}</h3>
        <CoreButton
          variant="contained"
          className="btn-round"
          startIcon={<AddIcon />}
          onClick={handleAddNewTask}
          disabled={disabled || isLoadingOperations}
          testo={t("AddTask")}
        />
      </div>

      <CoreTableEnhanced
        rows={tableRows}
        columns={columns}
        button={!disabled}
        actions={tableActions}
        isLoading={isLoadingOperations}
        idsSort={true}
        emptyMessage={t("NoTasksAvailable")}
        maxHeight={400}
        stickyHeader={true}
      />

      {/* Dialog per aggiungere/modificare task */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTask ? t("EditTask") : t("AddNewTask")}
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-4">
            <CoreInputField
              label={t("Name")}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
            />

            <CoreTextArea
              label={t("Description")}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              fullWidth
              rows={3}
              required
              error={Boolean(validationErrors.description)}
              helperText={validationErrors.description}
            />

            <CoreTextArea
              label={t("Note")}
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              fullWidth
              rows={2}
            />

            <CoreInputField
              label={t("EstimatedEffortHours")}
              name="estimatedEffort"
              type="number"
              value={formData.estimatedEffort}
              onChange={handleInputChange}
              fullWidth
              inputProps={{ min: 0, step: 0.5 }}
              error={Boolean(validationErrors.estimatedEffort)}
              helperText={validationErrors.estimatedEffort}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <CoreButton
            variant="outlined"
            onClick={handleCloseDialog}
            testo={t("Cancel")}
          />
          <CoreButton
            variant="contained"
            onClick={handleSaveTask}
            disabled={isLoadingOperations}
            testo={editingTask ? t("Save") : t("Add")}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ServiceTaskManager;
